/**
 * Sibling color swatch interactions for collection product cards.
 * - Hover: preview sibling's featured image (debounced restore to prevent flicker)
 * - Hover: prefetch sibling product data for instant click response
 * - Click: load all sibling images into the card's carousel with arrow navigation
 */
(function () {
  var productCache = {};
  var restoreTimers = new WeakMap();
  var originalSlides = new WeakMap();
  var loadingCards = new WeakSet();
  var prefetchTimer = 0;

  var ARROW_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>';

  function getCard(el) {
    return el.closest('product-card, .product-card');
  }

  function getCardGallery(card) {
    return card.querySelector('.card-gallery');
  }

  function getCardImg(card) {
    return card.querySelector('.card-gallery img, .product-media-container img');
  }

  function getSlideshow(card) {
    return card.querySelector('slideshow-component');
  }

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function resizeImageUrl(url, width) {
    if (!url) return url;
    return url.replace(/(_\d+x\d*|\d+x\d*)?(\.(jpg|jpeg|png|gif|webp|avif))/i, '_' + width + 'x$2');
  }

  function buildSlidesHTML(images) {
    var html = '';
    for (var i = 0; i < images.length; i++) {
      var img = images[i];
      var src = typeof img === 'string' ? img : img.src;
      var alt = (typeof img === 'object' && img.alt) ? img.alt : '';
      var ratio = (typeof img === 'object' && img.width && img.height) ? (img.width / img.height) : 1;
      var cardSrc = resizeImageUrl(src, 600);

      html += '<slideshow-slide ref="slides[]"'
        + ' aria-hidden="' + (i === 0 ? 'false' : 'true') + '"'
        + ' slide-id="bm-img-' + i + '"'
        + ' style="--slideshow-timeline: --slide-' + i + '; --product-media-fit: cover;"'
        + ' class="product-media-container media-fit product-media-container--image">'
        + '<div class="product-media" style="--ratio: ' + ratio + '">'
        + '<img src="' + cardSrc + '"'
        + ' alt="' + alt.replace(/"/g, '&quot;') + '"'
        + ' class="product-media__image"'
        + ' loading="' + (i === 0 ? 'eager' : 'lazy') + '">'
        + '</div></slideshow-slide>';
    }
    return html;
  }

  function ensureArrows(slideshow) {
    if (slideshow.querySelector('slideshow-arrows')) return;
    var container = slideshow.querySelector('slideshow-container');
    if (!container) return;
    var arrowsHTML = '<slideshow-arrows position="center">'
      + '<button class="slideshow-control slideshow-control--previous slideshow-control--style-arrow button button-unstyled button-unstyled--transparent flip-x" aria-label="Previous" on:click="/previous" ref="previous"><span class="svg-wrapper icon-arrow">' + ARROW_SVG + '</span></button>'
      + '<button class="slideshow-control slideshow-control--next slideshow-control--style-arrow button button-unstyled button-unstyled--transparent" aria-label="Next" on:click="/next" ref="next"><span class="svg-wrapper icon-arrow">' + ARROW_SVG + '</span></button>'
      + '</slideshow-arrows>';
    container.insertAdjacentHTML('afterbegin', arrowsHTML);
  }

  function removeArrows(slideshow) {
    var arrows = slideshow.querySelector('slideshow-arrows');
    if (arrows) arrows.remove();
  }

  function rebuildCarousel(card, productData, handle, url) {
    var slideshow = getSlideshow(card);
    if (!slideshow) return;

    var gallery = getCardGallery(card);
    if (!originalSlides.has(card)) {
      var scroller = slideshow.querySelector('[ref="scroller"], slideshow-slides');
      var origHandle = gallery && gallery.dataset.bmCurrentHandle;
      if (!origHandle) {
        var activeSwatch = card.querySelector('.bm-card-swatch--active');
        origHandle = activeSwatch ? activeSwatch.dataset.siblingHandle || '' : '';
      }
      originalSlides.set(card, {
        html: scroller ? scroller.innerHTML : '',
        hasArrows: !!slideshow.querySelector('slideshow-arrows'),
        handle: origHandle
      });
    }

    var images = productData.images || [];
    if (images.length === 0) return;

    var scroller = slideshow.querySelector('[ref="scroller"], slideshow-slides');
    if (!scroller) return;
    scroller.innerHTML = buildSlidesHTML(images);

    if (images.length > 2) {
      ensureArrows(slideshow);
    } else {
      removeArrows(slideshow);
    }

    slideshow.removeAttribute('disabled');
    slideshow.setAttribute('data-generic-media-size', String(images.length));

    var parent = slideshow.parentNode;
    var nextSibling = slideshow.nextSibling;
    parent.removeChild(slideshow);
    if (nextSibling) {
      parent.insertBefore(slideshow, nextSibling);
    } else {
      parent.appendChild(slideshow);
    }

    updateCardLinks(card, url);

    if (gallery) {
      gallery.dataset.bmCurrentHandle = handle;
    }

    var newImg = getCardImg(card);
    if (newImg) {
      newImg.dataset.originalSrc = newImg.src;
    }
  }

  function restoreOriginalCarousel(card) {
    var data = originalSlides.get(card);
    if (!data) return;

    var slideshow = getSlideshow(card);
    if (!slideshow) return;

    var scroller = slideshow.querySelector('[ref="scroller"], slideshow-slides');
    if (!scroller) return;
    scroller.innerHTML = data.html;

    if (!data.hasArrows) {
      removeArrows(slideshow);
    } else {
      ensureArrows(slideshow);
    }

    var parent = slideshow.parentNode;
    var nextSibling = slideshow.nextSibling;
    parent.removeChild(slideshow);
    if (nextSibling) {
      parent.insertBefore(slideshow, nextSibling);
    } else {
      parent.appendChild(slideshow);
    }

    var gallery = getCardGallery(card);
    if (gallery) {
      gallery.dataset.bmCurrentHandle = data.handle;
    }

    var activeSwatch = card.querySelector('.bm-card-swatch--active');
    if (activeSwatch && activeSwatch.dataset.siblingUrl) {
      updateCardLinks(card, activeSwatch.dataset.siblingUrl);
    }

    var newImg = getCardImg(card);
    if (newImg) {
      newImg.dataset.originalSrc = newImg.src;
    }
  }

  function updateCardLinks(card, url) {
    var galleryLink = card.querySelector('a[ref="cardGalleryLink"], .card-gallery > a.contents');
    if (galleryLink) galleryLink.href = url;

    var titleLink = card.querySelector('a[ref="productCardLink"], .product-card__link');
    if (titleLink) titleLink.href = url;

    var productTitle = card.querySelector('product-title a');
    if (productTitle) productTitle.href = url;
  }

  function updateActiveState(card, clickedSwatch) {
    var swatches = card.querySelectorAll('.bm-card-swatch');
    for (var i = 0; i < swatches.length; i++) {
      swatches[i].classList.remove('bm-card-swatch--active');
    }
    clickedSwatch.classList.add('bm-card-swatch--active');
  }

  // --- Hover preview (debounced) ---
  document.addEventListener('pointerenter', function (e) {
    if (e.pointerType === 'touch') return;
    var swatch = e.target.closest('.bm-card-swatch');
    if (!swatch || swatch.classList.contains('bm-card-swatch--active')) return;

    var card = getCard(swatch);
    if (!card) return;

    var pendingTimer = restoreTimers.get(card);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      restoreTimers.delete(card);
    }

    var img = getCardImg(card);
    if (!img) return;

    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.currentSrc || img.src;
    }
    img.src = swatch.dataset.siblingImage;
    img.removeAttribute('srcset');

    var handle = swatch.dataset.siblingHandle;
    if (handle) {
      clearTimeout(prefetchTimer);
      prefetchTimer = setTimeout(function () { fetchProduct(handle); }, 150);
    }
  }, true);

  // --- Hover leave (debounced restore) ---
  document.addEventListener('pointerleave', function (e) {
    if (e.pointerType === 'touch') return;
    var swatch = e.target.closest('.bm-card-swatch');
    if (!swatch) return;

    var card = getCard(swatch);
    if (!card) return;

    var img = getCardImg(card);
    if (!img || !img.dataset.originalSrc) return;

    var timer = setTimeout(function () {
      restoreTimers.delete(card);
      var currentImg = getCardImg(card);
      if (currentImg && currentImg.dataset.originalSrc) {
        currentImg.src = currentImg.dataset.originalSrc;
      }
    }, 80);

    restoreTimers.set(card, timer);
  }, true);

  // --- Click to load carousel ---
  document.addEventListener('click', function (e) {
    var swatch = e.target.closest('.bm-card-swatch');
    if (!swatch) return;

    var card = getCard(swatch);
    if (!card) return;

    e.preventDefault();
    e.stopPropagation();

    if (swatch.classList.contains('bm-card-swatch--active')) return;
    if (loadingCards.has(card)) return;

    var handle = swatch.dataset.siblingHandle;
    var url = swatch.dataset.siblingUrl;
    if (!handle || !url) {
      window.location.href = swatch.href;
      return;
    }

    var pendingTimer = restoreTimers.get(card);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      restoreTimers.delete(card);
    }

    var gallery = getCardGallery(card);
    var originalHandle;
    if (originalSlides.has(card)) {
      originalHandle = originalSlides.get(card).handle;
    } else if (gallery && gallery.dataset.bmCurrentHandle) {
      originalHandle = gallery.dataset.bmCurrentHandle;
    } else {
      var currentActive = card.querySelector('.bm-card-swatch--active');
      originalHandle = currentActive ? currentActive.dataset.siblingHandle || '' : '';
    }
    var isOriginal = handle === originalHandle;

    if (isOriginal && originalSlides.has(card)) {
      updateActiveState(card, swatch);
      restoreOriginalCarousel(card);
      return;
    }

    loadingCards.add(card);
    if (gallery) gallery.dataset.bmLoading = '';

    fetchProduct(handle).then(function (data) {
      loadingCards.delete(card);
      if (gallery) gallery.removeAttribute('data-bm-loading');

      if (!data || !data.images || data.images.length === 0) {
        window.location.href = url;
        return;
      }

      updateActiveState(card, swatch);
      rebuildCarousel(card, data, handle, url);
    }).catch(function () {
      loadingCards.delete(card);
      if (gallery) gallery.removeAttribute('data-bm-loading');
      window.location.href = url;
    });
  }, true);
})();
